import { getFloorY } from '@shared-lib/vanity-assembly/placementUtils';
import { metersToFeetInches } from '@shared-lib/dimensioning/feetInches';
import { VULGAR } from '@shared-lib/dimensioning/inchFractions';
import { roomDisplayName } from '@shared-lib/rooms/roomDisplayName';

/**
 * The architectural CUT PLANE: an object whose BOTTOM sits at or above this
 * height reads as "high" — dashed in plan, and drawn on the upper dimension
 * string in elevation. 48 inches, the conventional plan cut.
 */
export const HIGH_OBJECT_FLOOR_M = 48 * 0.0254;

// Helper function to create a matrix from a flat array
export const createMatrixFromArray = (array) => {
  if (!array || array.length !== 16) {
    return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  }
  return array;
};

// Helper function to apply a transformation matrix to a point
export const applyMatrixToPoint = (matrix, point) => {
  const [x, y, z] = point;

  const resultX = matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12];
  const resultY = matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13];
  const resultZ = matrix[2] * x + matrix[6] * y + matrix[10] * z + matrix[14];

  return [resultX, resultY, resultZ];
};

// Helper function to get the type of an item
export const getItemType = (item) => {
  if (!item) return 'Unknown';

  if (item.category) {
    if (item.category.wall) return 'Wall';
    if (item.category.door) return 'Door';
    if (item.category.window) return 'Window';
    if (item.category.bed) return 'Bed';
    if (item.category.table) return 'Table';
    if (item.category.chair) return 'Chair';
    if (item.category.storage) return 'Storage';
    if (item.category.television) return 'Television';
    // Add support for room types
    if (item.category.room) return 'Room';
    if (item.category.bedroom) return 'Bedroom';
    if (item.category.bathroom) return 'Bathroom';
    if (item.category.kitchen) return 'Kitchen';
    if (item.category.livingRoom) return 'Living Room';
    if (item.category.diningRoom) return 'Dining Room';
  }

  return 'Object';
};

// Helper function to get the category of an item
export const getCategory = (item) => {
  if (!item || !item.category) return 'Unknown';

  return Object.keys(item.category).join(', ');
};

const firstSectionLabel = (room) =>
  (room.sections || []).find((section) => section.label)?.label ?? null;

/**
 * A room's name, else its first labelled section's kind, exactly as stored —
 * for code that COMPARES it (the hallway finder's room kinds, the minimap's
 * test ids). Null when nothing names the room.
 */
export const storedRoomType = (room) =>
  room.name || firstSectionLabel(room) || null;

/**
 * What a person reads a room as: its name (set via Room Properties, or the
 * kind RoomPlan named it by), else its first labelled section's kind, the
 * way a person writes it ("livingRoom" → "Living room",
 * `@shared-lib/rooms/roomDisplayName`); 'Unknown' when nothing names it.
 */
export const getRoomType = (room, data) =>
  roomDisplayName(room.name) ??
  roomDisplayName(firstSectionLabel(room)) ??
  'Unknown';

// Find most frequent element in an array (used for rotation calculation)
export const findMostFrequentElement = (arr) => {
  const frequencyMap = {};
  let maxFrequency = 0;
  let mostFrequentElement;

  for (const element of arr) {
    frequencyMap[element] = (frequencyMap[element] || 0) + 1;

    if (frequencyMap[element] > maxFrequency) {
      maxFrequency = frequencyMap[element];
      mostFrequentElement = element;
    }
  }
  return mostFrequentElement;
};

export const drawLabel = (ctx, text, x, z, options = {}) => {
  // Default options
  const fontSize = options.fontSize || 14;
  const fontFamily = options.fontFamily || 'Arial';
  const color = options.color || '#333333';
  const backgroundColor = options.backgroundColor || 'rgba(255, 255, 255, 0.7)';
  const padding = options.padding || 5;
  const counterRotate = options.counterRotate !== false; // Default to true

  // Save current context state
  ctx.save();

  // Counter-rotate the text to keep it readable if needed
  if (counterRotate) {
    // Get the current transformation matrix
    const transform = ctx.getTransform();

    // Calculate the current rotation angle
    const angle = Math.atan2(transform.b, transform.a);

    // Apply counter-rotation
    ctx.translate(x, z);
    ctx.rotate(-angle);
    ctx.translate(-x, -z);
  }

  // Calculate the current scale (exclude DPR so labels stay consistent size)
  const dpr = window.devicePixelRatio || 1;
  const currentScale = ctx.getTransform().a / dpr;

  // Set up font and text properties with scale adjustment
  const scaledFontSize = fontSize / currentScale;
  ctx.font = `${scaledFontSize}px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Measure text for background
  const textWidth = ctx.measureText(text).width;
  const textHeight = scaledFontSize * 0.7;
  const scaledPadding = padding / currentScale;

  // Draw background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(
    x - textWidth / 2 - scaledPadding,
    z - textHeight / 2 - scaledPadding,
    textWidth + scaledPadding * 2,
    textHeight + scaledPadding * 2
  );

  // Draw text
  ctx.fillStyle = color;
  ctx.fillText(text, x, z);

  // Restore context state
  ctx.restore();
};

export const findRoomCorners = (room, rotation, tolerance = 0.05) => {
  // Extract wall endpoints and find corners
  const cornerPairs = findPairedWallCorners(room.walls);

  // Sort corners and find the interior polygon with variable wall offsets
  const sortedCorners = findRoomInterior(cornerPairs, rotation, tolerance);

  return {
    points:
      sortedCorners.interiorPolygonPoints ??
      sortedCorners.originalPolygonPoints,
    walls: sortedCorners.interiorPolygon,
  };
};

const findWallsForRoom = (room, walls) => {
  if (!room.sections || room.sections.length === 0) {
    return [];
  }

  // Get room bounds or center points for each section
  const roomBounds = [];
  const roomCenters = [];

  room.sections.forEach((section) => {
    if (section.bounds) {
      roomBounds.push(section.bounds);
    }
    if (section.center) {
      roomCenters.push(section.center);
    }
  });

  // Find walls that are within or at the boundary of the room sections
  return walls.filter((wall) => {
    const matrix = createMatrixFromArray(wall.transform);
    const dimensions = wall.dimensions || [1, 1, 0];
    const halfLength = dimensions[0] / 2;

    // Get wall direction from matrix
    const direction = [matrix[0], matrix[2]];
    const length = Math.sqrt(
      direction[0] * direction[0] + direction[1] * direction[1]
    );
    const normalizedDir = [direction[0] / length, direction[1] / length];

    // Get wall center and endpoints in world space
    const center = applyMatrixToPoint(matrix, [0, 0, 0]);
    const startPoint = [
      center[0] - normalizedDir[0] * halfLength,
      center[1],
      center[2] - normalizedDir[1] * halfLength,
    ];
    const endPoint = [
      center[0] + normalizedDir[0] * halfLength,
      center[1],
      center[2] + normalizedDir[1] * halfLength,
    ];

    // Check if the wall is part of any room section
    return (
      roomBounds.some((bounds) => {
        const [minX, minY, minZ, maxX, maxY, maxZ] = bounds;

        // Check if either endpoint of the wall is on the boundary of the room section
        const startOnBoundaryX =
          Math.abs(startPoint[0] - minX) < 0.01 ||
          Math.abs(startPoint[0] - maxX) < 0.01;
        const startOnBoundaryZ =
          Math.abs(startPoint[2] - minZ) < 0.01 ||
          Math.abs(startPoint[2] - maxZ) < 0.01;
        const endOnBoundaryX =
          Math.abs(endPoint[0] - minX) < 0.01 ||
          Math.abs(endPoint[0] - maxX) < 0.01;
        const endOnBoundaryZ =
          Math.abs(endPoint[2] - minZ) < 0.01 ||
          Math.abs(endPoint[2] - maxZ) < 0.01;

        return (
          startOnBoundaryX ||
          startOnBoundaryZ ||
          endOnBoundaryX ||
          endOnBoundaryZ
        );
      }) ||
      roomCenters.some((center) => {
        // If we have a center point but no bounds, check if the wall is reasonably close to the center
        const distToCenter = pointToLineDistance(
          [center[0], center[2]],
          [startPoint[0], startPoint[2]],
          [endPoint[0], endPoint[2]]
        );

        // Assume walls within 5 meters of center could be part of the room
        return distToCenter < 5;
      })
    );
  });
};

const findPairedWallCorners = (walls) => {
  if (!walls || walls.length === 0) {
    return [];
  }

  // Extract all wall endpoints
  const pairs = [];

  walls.forEach((wall) => {
    const matrix = createMatrixFromArray(wall.transform);
    const dimensions = wall.dimensions || [1, 1, 0];
    const halfLength = dimensions[0] / 2;

    // Get wall direction from matrix
    const direction = [matrix[0], matrix[2]];
    const length = Math.sqrt(
      direction[0] * direction[0] + direction[1] * direction[1]
    );
    const normalizedDir = [direction[0] / length, direction[1] / length];

    // Get wall center
    const center = applyMatrixToPoint(matrix, [0, 0, 0]);

    // Calculate endpoints
    const startX = center[0] - normalizedDir[0] * halfLength;
    const startZ = center[2] - normalizedDir[1] * halfLength;
    const endX = center[0] + normalizedDir[0] * halfLength;
    const endZ = center[2] + normalizedDir[1] * halfLength;

    pairs.push({
      points: [
        [startX, startZ],
        [endX, endZ],
      ],
      offset: dimensions[2] / 2,
      height: wall.dimensions[1],
      id: wall.id,
    });
  });

  return pairs;
};

/**
 * Checks if two points are within a specified tolerance of each other
 */
function withinTolerance(point1, point2, tolerance) {
  return (
    Math.abs(point1[0] - point2[0]) < tolerance &&
    Math.abs(point1[1] - point2[1]) < tolerance
  );
}

function findLineIntersection(p1, p2, p3, p4) {
  // Calculate direction vectors for both lines
  const v1 = [p2[0] - p1[0], p2[1] - p1[1]];
  const v2 = [p4[0] - p3[0], p4[1] - p3[1]];

  // Calculate magnitudes of vectors
  const mag1 = Math.sqrt(v1[0] * v1[0] + v1[1] * v1[1]);
  const mag2 = Math.sqrt(v2[0] * v2[0] + v2[1] * v2[1]);

  // Check for zero-length vectors
  if (mag1 < 1e-10 || mag2 < 1e-10) {
    return null;
  }

  // Normalize vectors
  const nv1 = [v1[0] / mag1, v1[1] / mag1];
  const nv2 = [v2[0] / mag2, v2[1] / mag2];

  // Calculate dot product to determine angle between lines
  const dotProduct = nv1[0] * nv2[0] + nv1[1] * nv2[1];

  // Cosine of 5 degrees is approximately 0.9962
  // If absolute value of dot product is greater than 0.9962,
  // the angle is less than 5 degrees (or more than 175 degrees)
  const cos5Degrees = Math.cos((5 * Math.PI) / 180);

  if (Math.abs(dotProduct) > cos5Degrees) {
    return null; // Lines are within 5 degrees of being parallel
  }

  // Standard line intersection calculation
  // Line 1 represented as a1x + b1y = c1
  const a1 = p2[1] - p1[1];
  const b1 = p1[0] - p2[0];
  const c1 = a1 * p1[0] + b1 * p1[1];

  // Line 2 represented as a2x + b2y = c2
  const a2 = p4[1] - p3[1];
  const b2 = p3[0] - p4[0];
  const c2 = a2 * p3[0] + b2 * p3[1];

  const determinant = a1 * b2 - a2 * b1;

  // Calculate intersection point of the infinite lines
  const x = (b2 * c1 - b1 * c2) / determinant;
  const y = (a1 * c2 - a2 * c1) / determinant;

  // Return the intersection
  return [x, y];
}

/**
 * Finds the intersection point of two line segments
 */
function findSegmentIntersection(p1, p2, p3, p4) {
  // Line 1 represented as a1x + b1y = c1
  const a1 = p2[1] - p1[1];
  const b1 = p1[0] - p2[0];
  const c1 = a1 * p1[0] + b1 * p1[1];

  // Line 2 represented as a2x + b2y = c2
  const a2 = p4[1] - p3[1];
  const b2 = p3[0] - p4[0];
  const c2 = a2 * p3[0] + b2 * p3[1];

  const determinant = a1 * b2 - a2 * b1;

  // If lines are parallel, no intersection
  if (Math.abs(determinant) < 1e-10) {
    return null;
  }

  const x = (b2 * c1 - b1 * c2) / determinant;
  const y = (a1 * c2 - a2 * c1) / determinant;

  // Check if the intersection point lies on both line segments
  const onSegment = (px, py, qx, qy, rx, ry) => {
    return (
      Math.min(px, qx) - 1e-6 <= rx &&
      rx <= Math.max(px, qx) + 1e-6 &&
      Math.min(py, qy) - 1e-6 <= ry &&
      ry <= Math.max(py, qy) + 1e-6
    );
  };

  // For numerical stability, add a small epsilon to the bounds check
  const epsilon = 1e-6;
  const extendSegment = (p, q) => {
    const dx = q[0] - p[0];
    const dy = q[1] - p[1];
    const length = Math.sqrt(dx * dx + dy * dy);

    // Avoid division by zero for extremely short segments
    if (length < 1e-10) return [p, q];

    const normDx = dx / length;
    const normDy = dy / length;
    return [
      [p[0] - normDx * epsilon, p[1] - normDy * epsilon],
      [q[0] + normDx * epsilon, q[1] + normDy * epsilon],
    ];
  };

  const [ext1Start, ext1End] = extendSegment(p1, p2);
  const [ext2Start, ext2End] = extendSegment(p3, p4);

  if (
    onSegment(ext1Start[0], ext1Start[1], ext1End[0], ext1End[1], x, y) &&
    onSegment(ext2Start[0], ext2Start[1], ext2End[0], ext2End[1], x, y)
  ) {
    return [x, y];
  }

  return null;
}

/**
 * Sorts corner pairs to form a continuous polygon
 */
function sortCorners(cornerPairs, rotation = 0, tolerance = 0.05) {
  // Empty input -> empty polygon. Without this guard, orderedWalls becomes
  // [undefined] and downstream .points access crashes when a room has no walls.
  if (!cornerPairs || cornerPairs.length === 0) {
    return { polygon: [], isClosed: false };
  }

  // Helper function for finding walls with a specific endpoint
  function findWallsWithEndpoint(walls, endpoint, tolerance) {
    const wallsWithEndpoint = [];
    for (const wall of walls) {
      if (withinTolerance(wall.points[0], endpoint, tolerance)) {
        wallsWithEndpoint.push(wall);
      }
    }
    return wallsWithEndpoint;
  }

  // Function to find the wall with the smallest counter-clockwise angle from a reference point
  function findWallWithSmallestCCWAngle(walls, referencePoint, origin) {
    if (!walls || walls.length === 0) {
      return null;
    }

    // Function to calculate angle of a point from origin (0 to 2π)
    function calculateAngle(point) {
      const dx = point[0] - origin[0];
      const dy = point[1] - origin[1];
      let angle = Math.atan2(dy, dx);
      if (angle < 0) {
        angle += 2 * Math.PI;
      }
      return angle;
    }

    // Calculate the reference angle
    const referenceAngle = calculateAngle(referencePoint);

    // Find the wall with the smallest counter-clockwise angle from the reference point
    let smallestAngleDiff = Infinity;
    let resultWall = null;

    for (const wall of walls) {
      const pointAngle = calculateAngle(wall.points[1]);

      // Calculate counter-clockwise angle difference (0 to 2π)
      let angleDiff = -(pointAngle - referenceAngle);
      if (angleDiff < 0) {
        angleDiff += 2 * Math.PI;
      }

      // Special case: very small angles (almost aligned)
      if (angleDiff < Math.PI / 180) {
        // 1 degree tolerance
        continue; // Skip this as it's essentially the same line
      }

      // Update result if this angle difference is smaller
      if (angleDiff < smallestAngleDiff) {
        smallestAngleDiff = angleDiff;
        resultWall = wall;
      }
    }

    return { wall: resultWall, angle: smallestAngleDiff };
  }

  // Clone the cornerPairs to avoid modifying the original
  const wallSegments = cornerPairs.map((pair) => ({
    height: pair.height,
    id: pair.id,
    points: [...pair.points],
    offset: pair.offset || 0,
    metadata: pair.metadata || {},
  }));

  // Create a copy with reversed points for considering opposite directions
  const oppositeDirectionWalls = wallSegments.map((wall) => ({
    height: wall.height,
    id: wall.id,
    points: [wall.points[1], wall.points[0]],
    offset: wall.offset || 0,
    metadata: wall.metadata || {},
  }));

  const allWalls = [...wallSegments, ...oppositeDirectionWalls];
  const remainingWalls = [...allWalls];

  // Initialize the ordered sequence with the first wall
  const orderedWalls = [remainingWalls[0]];
  remainingWalls.splice(0, 1);

  // Track the starting point for checking loop closure
  const startPoint = orderedWalls[0].points[0];

  let reverseTried = false;

  // Try to build a continuous path
  while (remainingWalls.length > 0) {
    const latestWall = orderedWalls[orderedWalls.length - 1];
    const latestEndpoint = latestWall.points[1];

    // Find walls that start where the latest wall ends
    const candidateWalls = findWallsWithEndpoint(
      remainingWalls,
      latestEndpoint,
      tolerance
    );

    // Check if we've completed the loop
    if (
      orderedWalls.length > 2 &&
      withinTolerance(latestEndpoint, startPoint, tolerance) &&
      candidateWalls.length === 0
    ) {
      break;
    }

    if (candidateWalls && candidateWalls.length > 0) {
      // Find the next wall with the smallest counter-clockwise angle
      const result = findWallWithSmallestCCWAngle(
        candidateWalls,
        latestWall.points[0], // Start point of current wall as reference
        latestEndpoint // End point of current wall as origin
      );

      if (result && result.wall) {
        orderedWalls.push(result.wall);
        const indexToRemove = remainingWalls.findIndex(
          (w) =>
            w === result.wall ||
            (withinTolerance(w.points[0], result.wall.points[0], tolerance) &&
              withinTolerance(w.points[1], result.wall.points[1], tolerance))
        );

        if (indexToRemove !== -1) {
          remainingWalls.splice(indexToRemove, 1);
        }

        // Also remove the opposite direction wall
        const oppositeIndex = remainingWalls.findIndex(
          (w) =>
            withinTolerance(w.points[0], result.wall.points[1], tolerance) &&
            withinTolerance(w.points[1], result.wall.points[0], tolerance)
        );

        if (oppositeIndex !== -1) {
          remainingWalls.splice(oppositeIndex, 1);
        }
      } else {
        // No valid next wall found
        if (!reverseTried) {
          // Try the reverse direction
          reverseTried = true;
          orderedWalls.reverse();
          // Also reverse the points of each wall
          orderedWalls.forEach((wall) => {
            wall.points = [wall.points[1], wall.points[0]];
          });
          continue;
        } else {
          break; // No solution found even after reversing
        }
      }
    } else if (!reverseTried) {
      // Try the reverse direction
      reverseTried = true;
      orderedWalls.reverse();
      // Also reverse the points of each wall
      orderedWalls.forEach((wall) => {
        wall.points = [wall.points[1], wall.points[0]];
      });
    } else {
      break; // No valid next wall found even after reversing
    }
  }

  // Check if the polygon is closed
  const isClosed =
    orderedWalls.length > 2 &&
    withinTolerance(
      orderedWalls[orderedWalls.length - 1].points[1],
      orderedWalls[0].points[0],
      tolerance
    );

  // // Apply rotation if specified
  // if (rotation !== 0) {
  //   const rotatePoint = (point) => {
  //     const cos = Math.cos(rotation);
  //     const sin = Math.sin(rotation);
  //     return [point[0] * cos - point[1] * sin, point[0] * sin + point[1] * cos];
  //   };

  //   orderedWalls.forEach((wall) => {
  //     wall.points[0] = rotatePoint(wall.points[0]);
  //     wall.points[1] = rotatePoint(wall.points[1]);
  //   });
  // }

  return {
    polygon: orderedWalls,
    isClosed,
  };
}

/**
 * Checks if a polygon is traversed in counter-clockwise order
 */
function isCounterClockwise(walls) {
  // Calculate the signed area of the polygon
  let area = 0;
  for (let i = 0; i < walls.length; i++) {
    const currentWall = walls[i];
    const nextWall = walls[(i + 1) % walls.length];

    const x1 = currentWall.points[0][0];
    const y1 = currentWall.points[0][1];
    const x2 = nextWall.points[0][0];
    const y2 = nextWall.points[0][1];

    area += x1 * y2 - x2 * y1;
  }

  // If area is positive, the polygon is CCW
  return area > 0;
}

/**
 * Offsets a line segment by a perpendicular distance
 */
function offsetLine(p1, p2, distance, toRight) {
  // Calculate direction vector of the line
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];

  // Calculate length of the line
  const length = Math.sqrt(dx * dx + dy * dy);

  // Guard against zero-length segments
  if (length < 1e-10) {
    return [p1, p2];
  }

  // Normalize direction vector
  const ndx = dx / length;
  const ndy = dy / length;

  // Calculate perpendicular vector
  let offsetX, offsetY;
  if (toRight) {
    offsetX = -ndy * distance;
    offsetY = ndx * distance;
  } else {
    offsetX = ndy * distance;
    offsetY = -ndx * distance;
  }

  // Offset the line
  return [
    [p1[0] + offsetX, p1[1] + offsetY],
    [p2[0] + offsetX, p2[1] + offsetY],
  ];
}

/**
 * Offsets the walls of a polygon toward its interior
 */
function offsetWallsToInterior(sortedWalls) {
  // Determine the winding direction
  const ccw = isCounterClockwise(sortedWalls);

  // Determine if we need to offset to the right or left based on traversal direction
  const offsetDirectionRight = ccw;

  // Apply offset to each wall using its individual offset value
  const offsetWalls = sortedWalls.map((wall) => {
    // Use the wall's individual offset property or default to 0 if not present
    const wallOffset = wall.offset !== undefined ? wall.offset : 0;

    const offsetPoints = offsetLine(
      wall.points[0],
      wall.points[1],
      wallOffset,
      offsetDirectionRight
    );

    return {
      ...wall,
      originalPoints: [...wall.points], // Keep original points for reference
      points: offsetPoints,
    };
  });

  // Fix the intersections between adjacent offset walls
  const fixedOffsetWalls = [];

  for (let i = 0; i < offsetWalls.length; i++) {
    const currentWall = offsetWalls[i];
    const nextWall = offsetWalls[(i + 1) % offsetWalls.length];

    // Add the current wall with its offset points
    fixedOffsetWalls.push({
      ...currentWall,
      points: [currentWall.points[0], currentWall.points[1]], // Create a new points array
    });

    // Find intersection of current and next offset walls
    const intersection = findLineIntersection(
      currentWall.points[0],
      currentWall.points[1],
      nextWall.points[0],
      nextWall.points[1]
    );

    if (intersection) {
      // Update endpoint of current wall
      fixedOffsetWalls[fixedOffsetWalls.length - 1].points[1] = intersection;

      // If it's the last wall, we need to update the first wall's starting point
      if (i === offsetWalls.length - 1) {
        fixedOffsetWalls[0].points[0] = intersection;
      } else {
        // Store the intersection point to be used for the next wall
        nextWall.points[0] = intersection;
      }
    }
  }

  return fixedOffsetWalls;
}

/**
 * Main function to find the interior polygon of a room
 */
export function findRoomInterior(cornerPairs, rotation = 0, tolerance = 0.05) {
  // First, sort the corners to form the room polygon
  const result = sortCorners(cornerPairs, rotation, tolerance);

  // Empty polygon (room with no walls): nothing to offset / extract.
  if (!result.polygon || result.polygon.length === 0) {
    return {
      success: false,
      originalPolygon: [],
      interiorPolygon: [],
      interiorPolygonPoints: [],
      originalPolygonPoints: [],
      isCounterClockwise: false,
    };
  }

  // Then, offset the walls toward the interior using each wall's individual offset
  const interiorWalls = offsetWallsToInterior(result.polygon);

  // Extract the interior polygon points
  const interiorPolygonPoints = interiorWalls.map((wall) => wall.points[0]);

  // Ensure the polygon is closed by adding the last point if needed
  if (
    !withinTolerance(
      interiorPolygonPoints[0],
      interiorWalls[interiorWalls.length - 1].points[1],
      tolerance
    )
  ) {
    interiorPolygonPoints.push(
      interiorWalls[interiorWalls.length - 1].points[1]
    );
  }

  // Extract the polygon points
  const originalPolygonPoints = interiorWalls.map((wall) => wall.points[0]);

  // Ensure the polygon is closed by adding the last point if needed
  if (
    !withinTolerance(
      originalPolygonPoints[0],
      interiorWalls[interiorWalls.length - 1].points[1],
      tolerance
    )
  ) {
    originalPolygonPoints.push(
      interiorWalls[interiorWalls.length - 1].points[1]
    );
  }

  return {
    success: true,
    originalPolygon: result.polygon,
    interiorPolygon: interiorWalls,
    interiorPolygonPoints,
    originalPolygonPoints,
    isCounterClockwise: isCounterClockwise(result.polygon),
  };
}

// Function to create a minimum rectangle containing all points from cornerPairs
export function createMinimumRectangle(points, rotation) {
  // If rotation is non-zero, rotate all points to find the axis-aligned bounding box
  const rotatedPoints = points.map(([x, y]) => {
    // Convert rotation from degrees to radians
    const radians = (rotation * Math.PI) / 180; // Negative to counter-rotate

    // Apply rotation transform
    const rotatedX = x * Math.cos(radians) - y * Math.sin(radians);
    const rotatedY = x * Math.sin(radians) + y * Math.cos(radians);

    return [rotatedX, rotatedY];
  });

  // Find min and max x, y coordinates in the rotated space
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  rotatedPoints.forEach(([x, y]) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });

  // Create the four corners of the rectangle in the rotated space
  const rotatedCorners = [
    [minX, minY], // top-left
    [maxX, minY], // top-right
    [maxX, maxY], // bottom-right
    [minX, maxY], // bottom-left
  ];

  // Rotate the corners back to the original orientation
  const radians = (-rotation * Math.PI) / 180;
  const finalCorners = rotatedCorners.map(([x, y]) => {
    const origX = x * Math.cos(radians) - y * Math.sin(radians);
    const origY = x * Math.sin(radians) + y * Math.cos(radians);

    return [origX, origY];
  });

  return finalCorners;
}

const pointToLineDistance = (point, lineStart, lineEnd) => {
  const [x, z] = point;
  const [x1, z1] = lineStart;
  const [x2, z2] = lineEnd;

  // Calculate line length
  const lineLength = Math.sqrt((x2 - x1) ** 2 + (z2 - z1) ** 2);

  if (lineLength === 0) {
    // Line is actually a point
    return Math.sqrt((x - x1) ** 2 + (z - z1) ** 2);
  }

  // Calculate distance
  const t = Math.max(
    0,
    Math.min(1, ((x - x1) * (x2 - x1) + (z - z1) * (z2 - z1)) / lineLength ** 2)
  );
  const projectionX = x1 + t * (x2 - x1);
  const projectionZ = z1 + t * (z2 - z1);

  return Math.sqrt((x - projectionX) ** 2 + (z - projectionZ) ** 2);
};

export const randomColor = () => {
  return '#' + Math.floor(Math.random() * 16777215).toString(16);
};

// Moved to shared-lib (pure; the functions-side opening schedule needs it).
export { metersToFeetInches };

// Parse feet-inches input back to meters. Inverse of metersToFeetInches.
// Accepts:
//   - Decimal feet:                     8.5             → 2.5908 m
//   - Whole feet:                       8'              → 2.4384 m
//   - Whole inches:                     6"              → 0.1524 m
//   - Feet + inches (with/without separators): 8' 6", 8'6", 8'-6"
//   - Feet + inches with fraction:      8'-6 1/2", 12'-6 ½" (the readout's glyphs)
//   - The inch mark left off after feet: 8'6, 8' 6 1/2
// Returns meters as a number, or null if the input can't be parsed. It reads
// the WHOLE input or nothing: dropping what it could not read after the feet
// turned `8'6` into 8'-0" and wrote a wall 6" short with no error.
const FRACTION_GLYPHS = Object.fromEntries(
  Object.entries(VULGAR).map(([text, glyph]) => [glyph, text])
);
const GLYPH_CLASS = Object.keys(FRACTION_GLYPHS).join('');
const FEET_INCHES_RE = new RegExp(
  "^(?:(\\d+(?:\\.\\d+)?)\\s*['′])?" + // feet
    `\\s*(?:(\\d+(?:\\.\\d+)?)(?=[\\s"″${GLYPH_CLASS}]|$))?` + // whole inches
    `\\s*(?:(\\d+)\\s*\\/\\s*(\\d+)|([${GLYPH_CLASS}]))?` + // fraction
    '\\s*(["″])?$' // inch mark
);

export const parseFeetInches = (input) => {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  if (s === '') return null;

  // Strip the dash separator some inputs use ("8'-6\"")
  const normalized = s.replace(/['′]\s*-\s*/g, "' ");

  // Decimal-feet shorthand: "8" or "8.5" with no quote characters
  if (!/['′"″]/.test(normalized)) {
    const n = Number(normalized);
    if (Number.isFinite(n) && n >= 0) return n * 0.3048;
    return null;
  }

  const m = normalized.match(FEET_INCHES_RE);
  if (!m) return null;
  const [, feetText, wholeText, num, den, glyph, inchMark] = m;
  // Inches with no feet in front of them need their mark: a bare `6` is
  // decimal feet, handled above.
  if (feetText === undefined && !inchMark) return null;
  if (feetText === undefined && !wholeText && !num && !glyph) return null;

  let fraction = 0;
  if (glyph) {
    const [gn, gd] = FRACTION_GLYPHS[glyph].split('/');
    fraction = Number(gn) / Number(gd);
  } else if (den !== undefined) {
    if (Number(den) === 0) return null;
    fraction = Number(num) / Number(den);
  }
  const feet = feetText === undefined ? 0 : parseFloat(feetText);
  const inches =
    (wholeText === undefined ? 0 : parseFloat(wholeText)) + fraction;
  if (!Number.isFinite(feet) || !Number.isFinite(inches)) return null;

  return feet * 0.3048 + inches * 0.0254;
};

/**
 * Find the floor Y for a structure or a single room.
 *
 * Delegates to the canonical `getFloorY(roomData, override)` in
 * `shared-lib/vanity-assembly/placementUtils.js` so web-app and
 * Cloud Functions agree on a single source of truth.
 *
 * **Resolution order** (most-specific to least):
 *  1. If `data` looks like a single room (has `floors`, no `rooms`),
 *     return `getFloorY(data)` directly.
 *  2. If `data` is a structure (`rooms[]`), walk rooms and return the
 *     first one with a floor — room-level floors are the canonical
 *     placement reference. *This is the bug-fix vs the old impl, which
 *     preferred `data.floors[0]` (a structure-wide aggregate) over
 *     individual room floors; the two can drift by a few mm and the
 *     structure-level value caused stale-Y placements.*
 *  3. As a last resort, fall back to `data.floors[0]` (legacy
 *     structure-level floor) for older structures that lack rooms.
 *  4. Returns 0 if nothing matched.
 *
 * For new code that already has the active room in scope, import
 * `getFloorY` directly from `@shared-lib/vanity-assembly/placementUtils`
 * — it's clearer about its room-scoped semantics.
 *
 * @param {Object} data - structureData or roomData
 * @returns {number} floor Y in meters
 */
export const findFloorY = (data) => {
  if (!data) return 0;
  // Single-room shape: has floors, no rooms array
  if (data.floors && !data.rooms) {
    return getFloorY(data) ?? 0;
  }
  // Structure shape: prefer per-room floors (the canonical placement
  // reference) over the structure-level aggregate.
  if (data.rooms) {
    for (const room of data.rooms) {
      if (room?.floors?.length > 0) {
        return getFloorY(room) ?? 0;
      }
    }
  }
  // Legacy structure-level floor (no rooms[] populated)
  if (data.floors?.length > 0) {
    const floor = data.floors[0];
    if (floor.center?.y != null) return floor.center.y;
    if (floor.polygonCorners?.[0]?.y != null) {
      return floor.polygonCorners[0].y;
    }
    if (floor.transform) {
      const matrix = createMatrixFromArray(floor.transform);
      return applyMatrixToPoint(matrix, [0, 0, 0])[1];
    }
  }
  return 0;
};

// "High" (≥48" above floor) is measured against the object's OWN room's
// floor. A single structure-wide reference put every object on the other
// story of a multi-story structure ≥48" up, ghosting a whole floor's
// furniture out of the all-floors view. Objects without room context
// (structure-level entries in 'both' mode) fall back to the structure-wide
// floor as before. Rooms without a floor polygon are left out of the map so
// their objects also use the fallback rather than a fabricated 0.
const buildRoomFloorYMap = (data) =>
  new Map(
    (data?.rooms || [])
      .filter((room) => room?.id && room?.floors?.length > 0)
      .map((room) => [room.id, getFloorY(room)])
  );

// Add this helper function to mark objects by height (instead of filtering)
export const filterObjectsByHeight = (objectsArray, data) => {
  if (!objectsArray) return [];

  // Check if we're using the new data format (center/rotation) or old format (transform)
  if (objectsArray.length > 0 && objectsArray[0].center) {
    // New format - filter based on center.y
    return filterObjectsByHeightV2(objectsArray, data);
  }

  const processedObjects = [];
  const structureFloorY = findFloorY(data);
  const roomFloorY = buildRoomFloorYMap(data);

  objectsArray.forEach((obj) => {
    if (obj.transform && obj.dimensions) {
      const matrix = createMatrixFromArray(obj.transform);
      const center = applyMatrixToPoint(matrix, [0, 0, 0]);
      const [width, height, depth] = obj.dimensions;

      // Calculate object's bottom Y position
      const objectBottomY = center[1] - height / 2;

      // Height above the object's own room floor (see buildRoomFloorYMap)
      const floorY = roomFloorY.get(obj._roomId) ?? structureFloorY;
      const heightAboveFloor = objectBottomY - floorY;

      const HEIGHT_THRESHOLD = HIGH_OBJECT_FLOOR_M;

      // Mark objects as high if they're above the threshold
      const processedObj = {
        ...obj,
        isHighObject: heightAboveFloor >= HEIGHT_THRESHOLD,
      };
      processedObjects.push(processedObj);
    }
  });

  return processedObjects;
};

// Helper function to mark objects by height for new data format
export const filterObjectsByHeightV2 = (objectsArray, data) => {
  if (!objectsArray) return [];

  const processedObjects = [];
  const structureFloorY = findFloorY(data);
  const roomFloorY = buildRoomFloorYMap(data);
  const maxHeightAboveFloor = HIGH_OBJECT_FLOOR_M;

  objectsArray.forEach((obj) => {
    if (obj.center && obj.dimensions) {
      // Calculate object's bottom Y position
      const objectBottomY = (obj.center.y || 0) - obj.dimensions.y / 2;

      // Height above the object's own room floor (see buildRoomFloorYMap)
      const floorY = roomFloorY.get(obj._roomId) ?? structureFloorY;
      const heightAboveFloor = objectBottomY - floorY;

      const processedObj = {
        ...obj,
        isHighObject: heightAboveFloor >= maxHeightAboveFloor,
      };
      processedObjects.push(processedObj);
    }
  });

  return processedObjects;
};

// Build a canvas-shaped wall-length measurement from a structure/room wall.
// Endpoints come from center + rotation.y + dimensions.x (same convention as
// useStretchPlan's boundary math). Used to re-open the stretch editor on the
// post-apply geometry so a locked measurement stays editable after unlock.
export const deriveWallLengthMeasurement = (wall) => {
  if (!wall?.center || !(wall?.dimensions?.x > 0)) return null;
  const ry = wall.rotation?.y || 0;
  const dx = Math.cos(ry);
  const dz = -Math.sin(ry);
  const half = wall.dimensions.x / 2;
  return {
    subtype: 'wall-length',
    wallId: wall.id,
    // `distance` is what the stretch math reads; `value` is what the panel
    // renders and seeds its input from. Real measurements out of
    // `calculateAllWallMeasurements` carry both, so a derived one must too —
    // without `value` the panel showed `NaN' NaN "` as the old length and left
    // its Distance field empty (which also tripped React's
    // uncontrolled-to-controlled warning).
    distance: wall.dimensions.x,
    value: wall.dimensions.x,
    start: [wall.center.x - dx * half, wall.center.z - dz * half],
    end: [wall.center.x + dx * half, wall.center.z + dz * half],
    label: metersToFeetInches(wall.dimensions.x),
  };
};
